import { useEffect, useState, useCallback, useRef, forwardRef } from 'react'
import { createPortal } from 'react-dom'
import { createEditor, Editor, Range } from 'slate'
import { Slate, Editable, withReact, useFocused, useSlate } from 'slate-react'
import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import { useReactiveVar } from '@apollo/client'
import isHotkey from 'is-hotkey'

import { MyEditor } from '../services/editor'
import { ReactComponent as BoldIcon } from '../assets/icons/bold-rounded-icon.svg'
import { ReactComponent as ItalicIcon } from '../assets/icons/italic-rounded-icon.svg'
import { ReactComponent as UnderlinedIcon } from '../assets/icons/underlined-rounded-icon.svg'
import { ReactComponent as ListNumberedIcon } from '../assets/icons/list-numbered-rounded-icon.svg'
import { ReactComponent as ListBulletedIcon } from '../assets/icons/list-bulleted-rounded-icon.svg'
import {
  apolloAuthData,
  getEditStatus,
  setEditStatus,
} from '../services/apollo'
import { ROLE_MANAGER } from '../constants'

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underlined',
}

const EditableComponent = ({
  editable = false,
  doc = '',
  setDoc = () => {},
  placeholder = 'Enter something...',
  tag = {},
  editableClassName = 'hover:outline-1 hover:outline-beer focus:outline-1 focus:outline-beer focus:shadow-sm',
  scrollbarY = true,
  registerChange = true,
  toolbarOption = {
    bold: true,
    italic: true,
    underline: true,
    listNumber: true,
    listBullet: true,
  },
  disableParagraph = false,
}) => {
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  const [editor] = useState(() => withReact(createEditor()))
  // const [changed, setChanged] = useState(false)
  let [searchParams] = useSearchParams()
  const isEditOn = isManager && !!searchParams.get('edit')
  const document = new DOMParser().parseFromString(doc, 'text/html')

  const renderElement = useCallback((props) => {
    switch (props.element.type) {
      case 'div':
        return <div {...props.attributes}>{props.children}</div>
      case 'numbered-list':
        return <ol {...props.attributes}>{props.children}</ol>
      case 'bulleted-list':
        return <ul {...props.attributes}>{props.children}</ul>
      case 'list-item':
        return <li {...props.attributes}>{props.children}</li>
      default:
        return <DefaultElement {...props} />
    }
  }, [])

  const renderLeaf = useCallback((props) => {
    return <Leaf {...props} />
  }, [])

  /**
   * Save changes to the reactive var
   *
   * @param {string} value - HTML string
   * @returns
   */
  const handleEditorChange = (value = '') => {
    // Stop if not in edit mode
    if (!isEditOn) {
      return
    }

    const serializedValue = MyEditor.serialize(
      { children: value },
      disableParagraph
    )
    const isChanged = serializedValue !== document.body.innerHTML

    // console.log('handleEditorChange :: isChanged', isChanged)

    if (setDoc) {
      setDoc(serializedValue)
    }

    if (registerChange) {
      const newChange = { ...tag, content: serializedValue }
      const changeIndex = getEditStatus().changes.findIndex((change) => {
        let found = change.page === tag.page && change.type === tag.type

        if (typeof tag.index === 'number') {
          found = found && tag.index === change.index
        }

        if (tag.id !== '') {
          found = found && tag.id === change.id
        }

        if (typeof tag.databaseId === 'number') {
          found = found && tag.databaseId === change.databaseId
        }

        return found
      })

      if (changeIndex > -1) {
        let updatedChanges = [...getEditStatus().changes]

        if (isChanged) {
          updatedChanges = getEditStatus().changes.map((change) => {
            if (
              change.page === tag.page &&
              change.type === tag.type &&
              (typeof tag.index ? change.index === tag.index : true) &&
              (tag.id !== '' ? change.id === tag.id : true) &&
              (typeof tag.databaseId === 'number'
                ? change.databaseId === tag.databaseId
                : true)
            ) {
              return newChange
            } else {
              return change
            }
          })
        } else {
          updatedChanges.splice(changeIndex, 1)
        }

        setEditStatus({
          ...getEditStatus(),
          changes: [...updatedChanges],
        })
      } else {
        if (isChanged) {
          setEditStatus({
            ...getEditStatus(),
            changes: [...getEditStatus().changes, newChange],
          })
        }
      }
    }

    // setChanged(isChanged)
  }

  // Wrap textNode in a <div> for Slate to work properly
  if (
    document.body.childNodes.length === 1 &&
    document.body.childNodes[0].nodeType === Node.TEXT_NODE
  ) {
    const div = document.createElement('div')
    wrap(document.body.childNodes[0], div)
  }

  return (
    <div
      className={clsx(
        'z-10 h-full max-h-full outline outline-0 outline-offset-8',
        scrollbarY && 'overflow-y-auto',
        isEditOn && editableClassName
      )}
    >
      <Slate
        editor={editor}
        value={MyEditor.deserialize(document.body)}
        onChange={handleEditorChange}
      >
        <FormattingToolbar
          editor={editor}
          bold={toolbarOption.bold}
          italic={toolbarOption.italic}
          underline={toolbarOption.underline}
          listNumber={toolbarOption.listNumber}
          listBullet={toolbarOption.listBullet}
        />
        <Editable
          placeholder={placeholder}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          readOnly={!editable}
          className="h-full [&_ol:not(.nostyle)]:mb-4 [&_ol:not(.nostyle)]:list-decimal [&_ol:not(.nostyle)]:pl-8 [&_p:not(:last-of-type)]:mb-5 [&_ul:not(.nostyle)]:mb-4 [&_ul:not(.nostyle)]:list-disc [&_ul:not(.nostyle)]:pl-8"
          // Useful for preventing <Disclosure/> from receiving events
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(event) => {
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault()
                const mark = HOTKEYS[hotkey]
                MyEditor.toggleFormat(editor, mark)
              }
            }
          }}
        />
      </Slate>
    </div>
  )
}

const DefaultElement = (props) => {
  return <p {...props.attributes}>{props.children}</p>
}

export const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>
  }

  if (leaf.italic) {
    children = <em>{children}</em>
  }

  if (leaf.underlined) {
    children = <u>{children}</u>
  }

  if (leaf.text === '\n') {
    children = <br />
  }

  return <span {...attributes}>{children}</span>
}

/**
 * Wrap `el` in a `wrapper`
 * Ref: https://dev.to/btopro/simple-wrap-unwrap-methods-explained-3k5f
 *
 * @param {*} el
 * @param {*} wrapper
 */
const wrap = (el, wrapper) => {
  if (el && el.parentNode) {
    el.parentNode.insertBefore(wrapper, el)
    wrapper.appendChild(el)
  }
}

/**
 *
 * @param {*}
 * @returns
 */
const Portal = ({ children }) => {
  return typeof document === 'object'
    ? createPortal(children, document.body)
    : null
}

const Menu = forwardRef(({ className, ...props }, ref) => (
  <div {...props} ref={ref} className={clsx(className, '[&_*+*]:ml-[15px]')} />
))

const FormatButton = ({ format, icon }) => {
  const editor = useSlate()
  return (
    <button onClick={() => MyEditor.toggleFormat(editor, format)}>
      {icon}
    </button>
  )
}

const BlockButton = ({ format, icon }) => {
  const editor = useSlate()
  return (
    <button onClick={() => MyEditor.toggleBlock(editor, format)}>{icon}</button>
  )
}

/**
 * Formatting toolbar
 * https://github.com/ianstormtaylor/slate/blob/main/site/examples/hovering-toolbar.tsx
 * @returns
 */
const FormattingToolbar = ({
  bold = true,
  italic = true,
  underline = true,
  listNumber = true,
  listBullet = true,
}) => {
  const ref = useRef()
  const editor = useSlate()
  const inFocus = useFocused()

  useEffect(() => {
    const el = ref.current
    const { selection } = editor

    if (!el) {
      return
    }

    if (
      !selection ||
      !inFocus ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ''
    ) {
      el.removeAttribute('style')
      return
    }

    const domSelection = window.getSelection()
    const domRange = domSelection.getRangeAt(0)
    const rect = domRange.getBoundingClientRect()
    el.style.opacity = '1'
    el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight}px`
    el.style.left = `${
      rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2
    }px`
  })

  return (
    <Portal>
      <Menu
        ref={ref}
        className="absolute -left-[10000px] -top-[10000px] z-40 -mt-[6px] rounded-[4px] bg-dark-charcoal px-[8px] py-[6px] text-xs text-white opacity-0 transition-opacity"
        onMouseDown={(e) => {
          // prevent toolbar from taking focus away from editor
          e.preventDefault()
        }}
      >
        {bold && (
          <FormatButton
            format="bold"
            icon={
              <BoldIcon
                className={clsx(
                  'h-[12px] w-[12px]',
                  MyEditor.isFormatActive(editor, 'bold')
                    ? 'fill-white'
                    : 'fill-dark-gray'
                )}
              />
            }
          />
        )}
        {italic && (
          <FormatButton
            format="italic"
            icon={
              <ItalicIcon
                className={clsx(
                  'h-[12px] w-[12px]',
                  MyEditor.isFormatActive(editor, 'italic')
                    ? 'fill-white'
                    : 'fill-dark-gray'
                )}
              />
            }
          />
        )}
        {underline && (
          <FormatButton
            format="underlined"
            icon={
              <UnderlinedIcon
                className={clsx(
                  'h-[12px] w-[12px]',
                  MyEditor.isFormatActive(editor, 'underlined')
                    ? 'fill-white'
                    : 'fill-dark-gray'
                )}
              />
            }
          />
        )}
        {listNumber && (
          <BlockButton
            format="numbered-list"
            icon={
              <ListNumberedIcon
                className={clsx(
                  'h-[12px] w-[12px]',
                  MyEditor.isBlockActive(editor, 'numbered-list')
                    ? 'fill-white'
                    : 'fill-dark-gray'
                )}
              />
            }
          />
        )}
        {listBullet && (
          <BlockButton
            format="bulleted-list"
            icon={
              <ListBulletedIcon
                className={clsx(
                  'h-[12px] w-[12px]',
                  MyEditor.isBlockActive(editor, 'bulleted-list')
                    ? 'fill-white'
                    : 'fill-dark-gray'
                )}
              />
            }
          />
        )}
      </Menu>
    </Portal>
  )
}

export default EditableComponent
